import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ExpandedDetailsModal,
  Modal,
  DynamicTable,
  TableCells,
  PageHeader,
  columnSortingFunctions,
  IconButton,
  CircleIconButton,
  Icons,
  StatusChip,
  PopupPosition,
  RelativePopupWrapper,
  TableInfoMenu as Menu,
  DatePickerRangeDropDown,
} from "@pushpress/shared-components";
import Skeleton from "react-loading-skeleton";
import moment from "moment";
import Dashboard from "../../../layouts/Dashboard";
import {
  getData,
  getSummary,
  getClasses,
  getPersons,
  editRegistration,
} from "../../../redux/actions/reportActions";
import CSVDownloader from "./CSVDownloader";
import FiltersMenu from "./FiltersMenu";

const rowsPerPage = 100;
const defaultFilters = { person: null, status: [], classType: [] };

const statusColor = {
  "no show": "error",
  "late cancel": "warning",
  "on time": "success",
  forgiven: "success",
  charged: "success",
};

const columns = [
  {
    name: "Name",
    dataKey: "fullName",
    skeletonWidthPct: 60,
    sortFunc: columnSortingFunctions.string,
    renderCell: (fullName) => {
      const idx = fullName.lastIndexOf(" ");
      const firstName = fullName.slice(0, idx);
      const lastName = fullName.slice(idx + 1);
      return (
        <TableCells.NameMonogramCell
          firstName={firstName}
          lastName={lastName}
        />
      );
    },
  },
  {
    name: "Class",
    dataKey: "classDate",
    skeletonWidthPct: 40,
    sortFunc: (a, b) => new Date(a).getTime() < new Date(b).getTime(),
    renderCell: (classDate) => {
      const date = moment(classDate);
      return (
        <TableCells.TitleSubtitleCell
          title={date.format("dddd h:mma")}
          subtitle={date.format("M/D/YYYY")}
        />
      );
    },
  },
  {
    name: "Status",
    dataKey: "status",
    skeletonWidthPct: 25,
    sortFunc: columnSortingFunctions.string,
    renderCell: (status) => {
      return (
        <TableCells.StatusChipCell label={status} color={statusColor[status]} />
      );
    },
  },
];
const oneWeekAgo = new Date();
oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
const today = new Date();

const asyncField = (value) =>
  value !== undefined ? value : <Skeleton width={32} />;
export function App(props) {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [userId, setUserId] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);
  const [classes, setClasses] = useState(null);
  const [persons, setPersons] = useState();
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const dateStart = useState(oneWeekAgo);
  const dateEnd = useState(today);
  const dateRangeStart = dateStart[0];
  const dateRangeEnd = dateEnd[0];

  const {
    // eslint-disable-next-line react/prop-types
    client: { uuid: clientId },
  } = props;

  useEffect(() => {
    getClasses(clientId).then((resp) => {
      const classList = resp.map((el) => {
        return { value: el, label: el };
      });
      setClasses(classList);
    });
  }, [clientId]);

  useEffect(() => {
    getPersons(clientId).then((resp) => {
      const userList = resp.map((el) => {
        return { value: el, option: el };
      });
      userList.unshift({
        value: null,
        option: "Show all",
      });

      setPersons(userList);
    });
  }, [clientId]);

  const closeModal = () => {
    setModalVisible(false);
    setSelectedRow(null);
  };

  const applyFilters = async (appliedFilters) => {
    setFiltersVisible(false);
    setFilters(appliedFilters);
  };

  const aggFilters = useMemo(
    () => ({
      ...filters,
      dateStart: dateRangeStart,
      dateEnd: dateRangeEnd,
    }),
    [filters, dateRangeStart, dateRangeEnd],
  );

  const fetchData = useCallback(
    (page) => {
      const startIdx = (page - 1) * rowsPerPage;
      return getData({
        clientId,
        from: startIdx,
        size: rowsPerPage,
        filters: aggFilters,
      });
    },
    [clientId, aggFilters, shouldRefresh],
  );

  useEffect(() => {
    if (!selectedRow) {
      return;
    }
    const fetchExtraAttributes = async () => {
      const promises = [];
      promises.push(getSummary(clientId, "no-show", userId));
      promises.push(getSummary(clientId, "no-show", userId, 90));
      promises.push(getSummary(clientId, "late-cancel", userId));
      promises.push(getSummary(clientId, "late-cancel", userId, 90));

      const [
        noShowSummary,
        noShowSummaryPeriod,
        lateCancelSummary,
        lateCancelSummaryPeriod,
      ] = await Promise.all(promises);

      if (selectedRow && selectedRow.userUuid === userId) {
        setSelectedRow((previous) => ({
          ...previous,
          noShowSummary,
          noShowSummaryPeriod,
          lateCancelSummary,
          lateCancelSummaryPeriod,
        }));
      }
    };
    fetchExtraAttributes();
  }, [userId]);

  const kebabMenu = (selectedDataRow, closePopup) => {
    const { status } = selectedDataRow;
    const viewProfile = () => {
      closePopup();
      window.location.href = selectedDataRow.memberUrl;
    };

    const viewClass = () => {
      closePopup();
      window.location.href = selectedDataRow.classUrl;
    };

    const createCharge = async () => {
      closePopup();

      if (["no show", "late cancel"].includes(status)) {
        const params = {
          removeLateCancel: 1,
          removeNoShow: 1,
          status: "charged",
          userUuid: selectedDataRow.userUuid,
          reservationUuid: selectedDataRow.reservationUuid,
          forgiven: 0,
          charged: 1,
          class: selectedDataRow.classType,
          date: selectedDataRow.classDate,
          fullName: selectedDataRow.fullName,
          pk: selectedDataRow.pk,
        };
        const resp = await editRegistration(params);
        console.log("resp", resp);
      } else {
        const message = `You cannot forgive a class with status: ${status}`;
        console.log("message", message);
      }

      window.open(selectedDataRow.createCharge, "_blank");
    };

    const forgiveLate = async () => {
      closePopup();

      const params = {
        removeLateCancel: 1,
        removeNoShow: 1,
        status: "forgiven",
        userUuid: selectedDataRow.userUuid,
        reservationUuid: selectedDataRow.reservationUuid,
        forgiven: 1,
        charged: 0,
        class: selectedDataRow.classType,
        date: selectedDataRow.classDate,
        fullName: selectedDataRow.fullName,
        pk: selectedDataRow.pk,
      };
      const resp = await editRegistration(params);
      if (resp === 1) {
        // delay to allow for update to propagate
        await new Promise((resolve) => setTimeout(resolve, 600));
        setShouldRefresh(!shouldRefresh);
      }
    };

    const clickInfo = async () => {
      closePopup();
      setSelectedRow(selectedDataRow);
      setUserId(selectedDataRow.userUuid);
      setModalVisible(true);
    };

    const actionTaken = ["forgiven", "charged"].includes(status);

    return (
      <Menu>
        <Menu.Item label="Info" action={clickInfo} />
        <Menu.Item label="View Profile" action={viewProfile} />
        <Menu.Item label="View Class" action={viewClass} />
        {!actionTaken && (
          <>
            <Menu.Item label="Create Charge" action={createCharge} />
            <Menu.Item label="Forgive" action={forgiveLate} />
          </>
        )}
      </Menu>
    );
  };

  const infoModal = selectedRow && (
    <ExpandedDetailsModal onClose={closeModal}>
      <ExpandedDetailsModal.MainContent title={selectedRow.fullName}>
        <ExpandedDetailsModal.Highlight
          icon={Icons.Clock}
          value={moment(selectedRow.classDate).format("dddd h:mma M/D/YYYY")}
        />
        <ExpandedDetailsModal.Highlight
          icon={Icons.Classes}
          value={selectedRow.classType}
        />
      </ExpandedDetailsModal.MainContent>
      <ExpandedDetailsModal.AttributeList>
        <ExpandedDetailsModal.Attribute
          field="Status"
          value={
            <StatusChip
              label={selectedRow.status}
              color={statusColor[selectedRow.status]}
            />
          }
        />
        <ExpandedDetailsModal.Attribute
          field="Reservation Created"
          value={moment(selectedRow.reservationCreated).format(
            "h:mma M/D/YYYY",
          )}
        />
        {selectedRow.status === "no show" && (
          <>
            <ExpandedDetailsModal.Attribute
              field="No-shows in last 90 days"
              value={asyncField(selectedRow.noShowSummaryPeriod)}
            />
            <ExpandedDetailsModal.Attribute
              field="Total No-shows"
              value={asyncField(selectedRow.noShowSummary)}
            />
          </>
        )}
        {selectedRow.status === "late cancel" && (
          <>
            <ExpandedDetailsModal.Attribute
              field="Time of Late Cancel"
              value={asyncField(
                moment(selectedRow.lateCancelDate).format("h:mma M/D/YYYY"),
              )}
            />
            <ExpandedDetailsModal.Attribute
              field="Late Cancels in last 90 days"
              value={asyncField(selectedRow.lateCancelSummaryPeriod)}
            />
            <ExpandedDetailsModal.Attribute
              field="Total Late Cancels"
              value={asyncField(selectedRow.lateCancelSummary)}
            />
          </>
        )}
      </ExpandedDetailsModal.AttributeList>
      <ExpandedDetailsModal.ActionMenu>
        <CircleIconButton
          icon={Icons.User}
          color="#0063ff"
          size={48}
          onClick={() => {
            window.location.href = selectedRow.memberUrl;
          }}
        />
      </ExpandedDetailsModal.ActionMenu>
    </ExpandedDetailsModal>
  );

  const filterMenu = (
    <FiltersMenu
      filters={filters}
      applyFilters={applyFilters}
      persons={persons}
      classes={classes}
      closeMenu={() => setFiltersVisible(false)}
    />
  );

  return (
    <Dashboard>
      <Modal visible={modalVisible} closeModal={closeModal}>
        {infoModal}
      </Modal>
      <div className="pp_appointments_parent">
        <PageHeader title="No Show / Late Cancel">
          <PageHeader.ActionMenu>
            <RelativePopupWrapper
              popupComponent={filterMenu}
              popupVisible={filtersVisible}
              popupPosition={PopupPosition.Bottom_Left_Outside}
              setPopupVisibility={setFiltersVisible}
              dismissOnOutsideClick={false}
            >
              <IconButton
                icon={Icons.Filter}
                tooltip="Filter"
                onClick={() => setFiltersVisible(true)}
              />
            </RelativePopupWrapper>
            <CSVDownloader client={clientId} filters={aggFilters} />
            <DatePickerRangeDropDown
              dateFrom={dateStart}
              dateTo={dateEnd}
              onChangeRange={() => console.log("changed")}
            />
          </PageHeader.ActionMenu>
        </PageHeader>
        <DynamicTable
          fetchData={fetchData}
          columns={columns}
          rowsPerPage={rowsPerPage}
          renderKebabMenu={kebabMenu}
        />
      </div>
    </Dashboard>
  );
}

export default App;
